import { HttpOptionsModel, TableSort } from '@amaris/lib-highway/models';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,Observable,catchError, map, of,
  tap,from, switchMap,
} from 'rxjs';
import { HttpClientService } from '@amaris/lib-highway/services';
import { ToastrService } from 'ngx-toastr';
import { ApiEndpoints } from '../../../enums/api-endpoints.enum';
import { environment } from '../../../../environments/environment';
import { ConsultationExtended } from '../../../models/consultation-extended.model';
import { ApiResponse } from '../../../models/api-response.model';
import { MovementAmplitude } from '../../../models/movement-amplitude.model';
import { ColorDropdown } from '../../../models/dropdown.model';
import { ActData } from '../../../models/act-data.model';
import { DataStoreService } from '../../data-store/data-store.service';
import { PainZoneExtended } from '../../../models/pain-zone-extended.model';
import { DocumentApiService } from '../document-api/document-api.service';
import { PatientExtended } from '../../../models/patient-extended.model';
import { User } from '../../../models/user.model';
import { ConsultationListItem } from '../../../models/consultations-list-item.model';
import { UsersApiService } from '../users-api/users-api.service';

@Injectable({
  providedIn: 'root',
})
export class ConsultationsApiService {
  constructor(
    private readonly httpClientService: HttpClientService,
    private readonly toastrService: ToastrService,
    private readonly dataStoreService: DataStoreService,
    private readonly documentApiService: DocumentApiService,
    private readonly usersApiService: UsersApiService,
  ) { }
  
  async loadSignatureBase64(signatureFile: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(signatureFile);
    });
  }

  generateInvoice(consultation: ConsultationExtended, patient: PatientExtended, user: User, example?: boolean): Observable<string> {
    const userId = user?.id;
    if (!userId) return of('');
    return this.documentApiService.getAllByParentid(userId).pipe(
      map((documents) => {
        const signatureDocs = documents.filter(
          (doc) => Array.isArray(doc.tags) && doc.tags.includes('Signature')
        );
        return signatureDocs.length ? signatureDocs.at(-1) : null;
      }),
      switchMap((signatureDoc) => {
        if (!signatureDoc?.id) return of('');
        return this.documentApiService.downloadById(signatureDoc.id).pipe(
          switchMap((blob: Blob) => {
            const file = new File([blob], signatureDoc.name || 'signature.png', { type: blob.type });
            return from(this.loadSignatureBase64(file));
          })
        );
      }),
      catchError((error) => {
        return of('');
      }),
      switchMap((signatureBase64: string) => {
        const practice = this.dataStoreService.currentMedialPractice$.value;
        const options: HttpOptionsModel = {
          url: `${environment.apiUrl}/billings`,
          body: {
            paymentMethod: consultation.paymentMethod,
            consultationReason: consultation.consultationReason,
            consultationPrice: consultation.consultationPrice,
            consultationCity: practice?.city,
            consultationDate: consultation.createdAt,
            consultationId: consultation.id,
            companyName: practice?.companyName,
            officeAddress: `${practice?.officeAddress}, ${practice?.postalCode}, ${practice?.city}`,
            siret: practice?.siret,
            adeli: user.adeliNumber,
            rpps: user.rpps,
            practitionerFirstName: user.firstname,
            practitionerLastName: user.lastname,
            practitionerEmail: user.email,
            patientFirstName: patient.firstname,
            patientLastName: patient.lastname,
            patientPhoneNumber: patient.phone,
            patientEmail: patient.email,
            patientBillingAddress: patient.address,
            patientBirthDate: patient.birthDate.toISOString(),
            maidenName: patient.maidenName,
            socialSecurityNumber: patient.socialSecurityNumber,
            billingStatus: null,
            practitionerProfession: user.status,
            isExample: example,
            signatureBase64: user.signature64 ?? signatureBase64,
          },
          withCredentials: true,
        };
        return this.httpClientService.post<ApiResponse<any>>(options).pipe(
          map((response) => response.data),
          catchError(() => {
            this.toastrService.error('Une erreur est survenue lors de la création de la facture. Veuillez réessayer plus tard.');
            return of(null);
          })
        );
      })
    );
  }

  createConsultation(patientId: string, consultation: ConsultationExtended): Observable<string> {
    consultation.patientId = patientId;

    return this.usersApiService.getConnectedUser().pipe(
      tap((user) => {
        consultation.praticienFirstName = user.data.firstname;
        consultation.praticienLastName = user.data.lastname;
      }),
      switchMap(() => {
        const tmpConsultation = { ...consultation };
        delete tmpConsultation.anamneseRecordings;
        delete tmpConsultation.examsRecordings;
        delete tmpConsultation.actsRecordings;
        const options: HttpOptionsModel = {
          url: environment.apiUrl + ApiEndpoints.Consultations,
          body: tmpConsultation,
          withCredentials: true,
        };

        return this.httpClientService.post<ApiResponse<string>>(options).pipe(
          map((response) => response.data), // extract the data property
        );
      }),
      tap((response) => {
        this.toastrService.success('Consultation enregistrée avec succès');
      }),
      catchError((error) => {
        this.toastrService.error('Une erreur est survenu lors de la création de la consultation. Veuillez réessayer plus tard.');
        return of(null);
      }),
    );
  }

  updateConsultation(consultation: ConsultationExtended): Observable<string> {
    const tmpConsultation = { ...consultation };
    delete tmpConsultation.anamneseRecordings;
    delete tmpConsultation.examsRecordings;
    delete tmpConsultation.actsRecordings;
    delete tmpConsultation.checkupRecordings;

    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.Consultations}/${consultation.id}`,
      body: tmpConsultation,
      withCredentials: true,
    };

    return this.httpClientService.put<ApiResponse<string>>(options).pipe(
      map((response) => {
        this.toastrService.success('Consultation modifiée avec succès');
        return response.data;
      }),
      catchError(() => {
        this.toastrService.error('Une erreur est survenu lors de la modification de la consultation. Veuillez réessayer plus tard.');
        return of(null);
      }),
    );
  }

  patchConsultation(partialConsultation: Partial<ConsultationExtended>): Observable<string> {
    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.Consultations}/${partialConsultation.id}`,
      body: partialConsultation,
      withCredentials: true,
    };

    return this.httpClientService.patch<ApiResponse<string>>(options).pipe(
      map((response) => {
        this.toastrService.success('Consultation modifiée avec succès');
        return response.data;
      }),
      catchError(() => {
        this.toastrService.error('Une erreur est survenu lors de la modification de la consultation. Veuillez réessayer plus tard.');
        return of(null);
      }),
    );
  }

  getConsultationsByRoomId(roomId: string): Observable<ConsultationExtended[]> {
    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.Consultations}/room/${roomId}`,
      withCredentials: true,
    };

    return this.httpClientService.get<ApiResponse<ConsultationExtended[]>>(options).pipe(
      map((response) => {
        return response?.data ?? [];
      }),
    );
  }

  getConsultationsByPatientId(patientId: string): Observable<any> {
    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.Consultations}`,
      params: { patientId },
      withCredentials: true,
    };

    return this.httpClientService.get<ApiResponse<any>>(options).pipe(
      map((response) => {
        const data = response?.data
          ? response.data.sort((a: any, b:any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          : [];
        data.forEach((consultation: any) => {
          consultation.painZones = consultation.painZones.map((zone: any) => new PainZoneExtended(zone));
          consultation.propagatedPainZones = consultation.propagatedPainZones
            .map((list: PainZoneExtended[]) => list.map((zone: PainZoneExtended) => new PainZoneExtended(zone)));
        });
        return data;
      }),
      tap((consultations) => {
        this.dataStoreService.setCurrentPatientExtendedConsultations(consultations);
      }),
    );
  }

  getConsultationTypes(): Observable<ColorDropdown[]> {
    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.ConsultationTypes}`,
      withCredentials: true,
    };

    return this.httpClientService.get<ApiResponse<any>>(options).pipe(
      map((response) => response.data.map((type: any) => (
        new ColorDropdown(type.label, type.id, type.colorCode)
      ))),
      tap((types) => {
        this.dataStoreService.setConsultationTypes(types);
      }),
    );
  }

  getConsultationActesData(): Observable<ActData[]> {
    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.ConsultationActs}`,
      withCredentials: true,
    };

    return this.httpClientService.get<ApiResponse<ActData[]>>(options).pipe(
      map((response) => response.data),
      tap((actes) => {
        this.dataStoreService.setConsultationActeData(actes);
      }),
    );
  }

  getConsultationsFiltered(query: string): Observable<ConsultationListItem[]> {
      const options: HttpOptionsModel = {
        url: environment.apiUrl + ApiEndpoints.Consultations,
        params: { query },
        withCredentials: true,
      };
  
      return this.httpClientService.get<ApiResponse<ConsultationListItem[]>>(options).pipe(
        map((response) => response.data),
        tap((consultations) => {
          this.dataStoreService.setConsultations(consultations);
        }),
      );
  }

  getConsultationById(consultationId: string, skipDocuments: boolean = false): Observable<ConsultationExtended> {
    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.Consultations}/${consultationId}`,
      withCredentials: true,
    };

    return this.httpClientService.get<ApiResponse<ConsultationExtended>>(options).pipe(
      map((response) => {
        response.data.examsManipulations = response.data.examsManipulations.map((manip) => new MovementAmplitude(
          manip.id,
          manip.icon,
          manip.title,
          manip.value,
          manip.unit,
          null,
          manip.bodyZoneId,
        ));
        response.data.bilanManipulations = response.data.bilanManipulations.map((manip) => new MovementAmplitude(
          manip.id,
          manip.icon,
          manip.title,
          manip.value,
          manip.unit,
          null,
          manip.bodyZoneId,
        ));
        response.data.painZones = response.data.painZones.map((zone) => new PainZoneExtended(zone));

        response.data.propagatedPainZones = response.data.propagatedPainZones.map((list) => list.map((zone) => new PainZoneExtended(zone)));

        if (!skipDocuments) {
          return new ConsultationExtended(response.data, this.documentApiService.getAllByParentid(response.data.id));
        }

        return new ConsultationExtended(response.data);
      }),
    ).pipe(catchError(() => of(null)));
  }

  getPaginatedConsultationsFilteredSorted(
    itemsPerPage: number,
    requestedPage: number,
    sort: TableSort = null,
    filter: string = '',
  ): Observable<ApiResponse<ConsultationListItem[]>> {
    const params: any = {
      'page[limit]': itemsPerPage,
      'page[current]': requestedPage,
    };
    if (sort && sort.sortOrder) {
      params['sort'] = (sort.sortOrder === 'desc' ? '-' : '') + sort.sortBy;
    }
    if (filter) {
      params['query'] = filter;
    }
    const options: HttpOptionsModel = {
      url: `${environment.apiUrl + ApiEndpoints.Consultations}`,
      params,
      withCredentials: true,
    };

    return this.httpClientService.get<ApiResponse<ConsultationListItem[]>>(options).pipe(
      tap((response) => {
        if (response) {
          response.data.forEach((consultation: ConsultationListItem) => {
            consultation.createdAt = new Date(consultation.createdAt);
          });
        }
      }),
    );
  }

  private _isSaved = new BehaviorSubject<boolean>(true); 
  isSaved$ = this._isSaved.asObservable();

  setSavedState(state: boolean) {
    this._isSaved.next(state);
  }

  get isSaved(): boolean {
    return this._isSaved.value;
  }
}
